const MS_PER_DAY = 86_400_000

/**
 * Only events stored with date granularity are full-day, regardless of duration.
 */
export function isFullDayEvent(dateGranularity: string | undefined): boolean {
  return dateGranularity === 'date'
}

/**
 * Formats an event's time span, e.g. "08:00 - 23:00", with "(+N)" when it ends N days later.
 */
export function formatEventTimeRange(
  start: Date,
  end: Date,
  locale: string,
  timeZone: string
): string {
  const timeFormat = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
    timeZone
  })

  const dateFormat = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone
  })

  const formattedStart = timeFormat.format(start)
  const formattedEnd = timeFormat.format(end)
  const dayOffset = getCalendarDay(end, dateFormat) - getCalendarDay(start, dateFormat)

  if (dayOffset <= 0) {
    return formattedStart === formattedEnd
      ? formattedStart
      : `${formattedStart} - ${formattedEnd}`
  }

  return `${formattedStart} - ${formattedEnd} (+${dayOffset})`
}

/**
 * Days since epoch for the date's calendar day in the formatter's time zone.
 */
function getCalendarDay(date: Date, dateFormat: Intl.DateTimeFormat): number {
  const parts = dateFormat.formatToParts(date)
  const getPart = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value)

  return Date.UTC(getPart('year'), getPart('month') - 1, getPart('day')) / MS_PER_DAY
}
